import { ReactNode, useCallback, useMemo } from "react";
import { useGlobalDialogContext } from "./GlobalDialogContext";
import { BlocBuilder } from "@kernel/frontend-react-app";
import {
  DialogSizeType,
  DialogState,
  FullscreenDialogType
} from "./GlobalDialogState";
import { ModalProps } from "react-bootstrap";
import { IStack } from "@kernel/utils";

export interface UseGlobalDialogBloc {
  stack: IStack<DialogState>;
  activeDialog: DialogState | null;
  onOpen: (
    component: ReactNode,
    size?: DialogSizeType,
    fullscreen?: FullscreenDialogType,
    props?: any
  ) => void;
  onOpenSmall: TOpenHandler;
  onOpenLarge: TOpenHandler;
  onOpenExtraLarge: TOpenHandler;
  onOpenFullScreen: TOpenHandler;
  onClose: () => void;
  onCloseStack: () => void;
}

type TOpenHandler = (
  component: ReactNode,
  props?: Omit<ModalProps, "bakdrop" | "children">
) => void;

export function useGlobalDialogBloc(): UseGlobalDialogBloc {
  const bloc = useGlobalDialogContext();
  const useBloc = BlocBuilder(bloc);

  const state = useBloc();

  const activeDialog = useMemo(() => state.dialogs.head(), [state]);

  const createOpenHandler = useMemo(
    () =>
      (
        size: DialogSizeType,
        fullscreen?: FullscreenDialogType
      ): TOpenHandler => {
        return useCallback(
          (
            component: ReactNode,
            props?: Omit<ModalProps, "bakdrop" | "children">
          ) => {
            const dialogProps: Omit<ModalProps, "bakdrop" | "children"> = {
              onClose: props?.onClose || handleClose,
              ...props
            };
            bloc.open({ component, size, props: dialogProps, fullscreen });
          },
          [bloc]
        );
      },
    [bloc]
  );

  const handleOpen = useCallback(
    (
      component: ReactNode,
      size: DialogSizeType = "lg",
      fullscreen: FullscreenDialogType = undefined,
      props: Omit<ModalProps, "bakdrop" | "children"> = {}
    ) => {
      props.onClose = props.onClose || handleClose;
      bloc.open({ component, size, props, fullscreen });
    },
    [bloc]
  );

  const handleClose = useCallback(() => {
    bloc.close();
  }, [bloc]);

  const handleCloseStack = useCallback(() => {
    bloc.closeAll();
  }, [bloc]);

  return {
    stack: state.dialogs,
    activeDialog,
    onOpen: handleOpen,
    onOpenSmall: createOpenHandler("sm"),
    onOpenLarge: createOpenHandler("lg"),
    onOpenExtraLarge: createOpenHandler("xl"),
    onOpenFullScreen: createOpenHandler("xl", true),
    onClose: handleClose,
    onCloseStack: handleCloseStack
  };
}
